/* eslint-disable no-case-declarations */
/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState, useEffect, useMemo } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { Splide as SplideCore } from '@splidejs/splide';
import ossImgCompress from '@/utils/ossImgCompress';
import { LoadingOutlined } from '@ant-design/icons';
import styles from './index.module.less';
import { ClassNames, isBroswer } from '@/utils';
import { useGetConfig } from '../../useGetConfig';
import { ProductTracker } from '@/utils/Tracker';

interface ExtendedSplide extends Splide {
  videoEle?: HTMLVideoElement[];
}

interface Pamran {
  curIndex?: number;
  prevIndex?: number;
  splide?: SplideCore;
  newIndex?: number;
}

const EXPOSE_VIDEO = new Map([]);

const MasterVideo = () => {
  const {
    mobxStore: { app, product },
    masterVideoList,
  } = useGetConfig();
  const { webp } = app;
  const { curSelectCommodityId, productDetail, curSelectCommodity } = product;
  const splideRef = useRef<ExtendedSplide | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isEnablePIP = isBroswer ? !!document.exitPictureInPicture : true;

  const loadVideo = (ele: HTMLVideoElement | undefined) => {
    if (!ele || !ele?.dataset.mastersrc) return;
    ele.src = ele.dataset.mastersrc;
  };

  useEffect(() => {
    /** 初始化视频 */
    handleVideo('video_register_event');
    /** 暂停所有视频 */
    handleVideo('video_stop_all');
    /** 切换时不在第一个需要调回去 */
    if (activeIndex) {
      splideRef.current?.splide?.go(0);
      setActiveIndex(0);
    }

    return () => {
      if (splideRef.current?.videoEle?.length) {
        splideRef.current?.videoEle?.forEach((video) => video.remove());
        splideRef.current.videoEle = [];
      }

      /** 切换套餐时，关闭上一个画中画 */
      try {
        if (document.pictureInPictureElement && document.exitPictureInPicture) {
          document.exitPictureInPicture();
        }
      } catch (e) {
        // 退出画中画模式可能失败
        console.error(e);
      }
    };
  }, [curSelectCommodityId]);

  /** 过滤出当前套餐的主视频 */
  const mainVideoList = useMemo(() => {
    return masterVideoList.map((o) => {
      o.coverLink = ossImgCompress(o.coverLink, { webp });
      o.coverPreviewLinkPc = ossImgCompress(o.coverPreviewLinkPc, { webp });
      return o;
    });
  }, [curSelectCommodityId, masterVideoList]);

  const handleVideo = (type: string, params?: Pamran) => {
    if (!type || !splideRef.current) return;
    switch (type) {
      /** 视频元素注册事件 */
      case 'video_register_event':
        const videoList = Array.from(document.querySelectorAll('#mainVideo')) as HTMLVideoElement[];
        splideRef.current.videoEle = [...videoList];
        splideRef.current.videoEle.forEach((videoItem) => {
          const onEnded = () => {
            videoItem.currentTime = 0;
            ProductTracker.productPageImages({
              action: 'video_end',
              category: 'product_image',
              data: {
                url_key: productDetail?.url_key,
                name: curSelectCommodity?.name,
                id: curSelectCommodityId,
                videoInnerName: videoItem?.dataset?.innername,
              },
            });
          };

          const onLoaded = () => {
            videoItem.play();
          };

          const onPlay = () => {
            if (videoItem.parentNode) {
              (videoItem.parentNode as HTMLElement).dataset.playing = 'true';
            }
          };
          const onLeavepictureinpicture = (e: Event) => {
            (e.currentTarget as HTMLVideoElement)?.pause?.();
          };
          videoItem.remove = () => {
            videoItem.removeEventListener('ended', onEnded);
            videoItem.removeEventListener('loadeddata', onLoaded);
            videoItem.removeEventListener('play', onPlay);
            videoItem.removeEventListener('leavepictureinpicture', onLeavepictureinpicture);
          };
          videoItem.addEventListener('ended', onEnded);

          videoItem.addEventListener('loadeddata', onLoaded);

          videoItem.addEventListener('play', onPlay);

          videoItem.addEventListener('leavepictureinpicture', onLeavepictureinpicture);
        });
        break;
      /** 视频轮播切换时 */
      case 'video_move':
        const { newIndex, prevIndex } = params ?? { newIndex: 0, prevIndex: 0 };
        if (splideRef.current.videoEle && (prevIndex || prevIndex === 0)) {
          splideRef.current.videoEle?.[prevIndex]?.pause();
          splideRef.current.videoEle[prevIndex].currentTime = 0;
          splideRef.current.videoEle[prevIndex].src = '';
          splideRef.current.videoEle[prevIndex].load();
          if (splideRef.current.videoEle[prevIndex].parentNode) {
            const parentNode = splideRef.current.videoEle[prevIndex].parentNode as HTMLElement;
            parentNode.dataset.clickplay = 'false';
            parentNode.dataset.playing = 'false';
          }

          if (newIndex || newIndex === 0) {
            if (!EXPOSE_VIDEO.has(mainVideoList?.[newIndex]?.id)) {
              EXPOSE_VIDEO.set(mainVideoList[newIndex]?.id, true);
            }
          }
        }

        break;
      /** 停止所有视频播放 */
      case 'video_stop_all':
        splideRef.current?.videoEle?.forEach((item) => {
          item.pause();
          item.currentTime = 0;
          item.src = '';
          item.load();
          if (item.parentNode) {
            const parentNode = item.parentNode as HTMLElement;
            parentNode.dataset.clickplay = 'false';
            parentNode.dataset.playing = 'false';
          }
        });
        break;
      /** 点击视频播放 */
      case 'video_play':
        const { curIndex } = params ?? { curIndex: 0 };
        if (curIndex || curIndex === 0) {
          if (splideRef.current?.videoEle && splideRef.current.videoEle[curIndex]?.parentNode) {
            const parentNode = splideRef.current.videoEle[curIndex].parentNode as HTMLElement;
            parentNode.dataset.clickplay = 'true';
          }
          loadVideo(splideRef.current?.videoEle?.[curIndex]);
          splideRef.current?.videoEle?.[curIndex].play();

          ProductTracker.productPageImages({
            action: 'click_video_button',
            category: 'product_image',
            data: {},
          });

          ProductTracker.productPageImages({
            action: 'video_play',
            category: 'product_image',
            data: {
              url_key: productDetail?.url_key,
              name: curSelectCommodity?.name,
              id: curSelectCommodityId,
              videoInnerName: splideRef.current?.videoEle?.[curIndex].dataset.innername,
            },
          });
        }
        break;
    }
  };

  return (
    <div className={styles.carousel}>
      <div className={styles.container}>
        {mainVideoList.length > 1 && (
          <div className={ClassNames([styles.arrow, styles.arrowL])} onClick={() => splideRef.current?.splide?.go('<')}>
            <svg
              className={styles.arrSvg}
              width='49'
              height='48'
              viewBox='0 0 49 48'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_9392_51685)'>
                <path
                  className={styles.arrPath}
                  d='M24.0723 48C37.3271 48 48.0723 37.2548 48.0723 24C48.0723 10.7452 37.3271 0 24.0723 0C10.8174 0 0.0722656 10.7452 0.0722656 24C0.0722656 37.2548 10.8174 48 24.0723 48Z'
                  fill='black'
                  // fillOpacity='0.3'
                />
                <path
                  d='M25.0723 29L20.0723 24L25.0723 19'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  fillOpacity={1}
                />
              </g>
              <defs>
                <clipPath id='clip0_9392_51685'>
                  <rect width='48' height='48' fill='white' transform='translate(0.0722656)' />
                </clipPath>
              </defs>
            </svg>
          </div>
        )}
        {mainVideoList.length > 1 && (
          <div className={ClassNames([styles.arrow, styles.arrowR])} onClick={() => splideRef.current?.splide?.go('>')}>
            <svg
              className={styles.arrSvg}
              width='49'
              height='48'
              viewBox='0 0 49 48'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g clipPath='url(#clip0_9392_51688)'>
                <path
                  className={styles.arrPath}
                  d='M24.9277 48C11.6729 48 0.927734 37.2548 0.927734 24C0.927734 10.7452 11.6729 0 24.9277 0C38.1826 0 48.9277 10.7452 48.9277 24C48.9277 37.2548 38.1826 48 24.9277 48Z'
                  fill='black'
                  // fillOpacity='0.3'
                />
                <path
                  d='M23.9277 29L28.9277 24L23.9277 19'
                  stroke='white'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  fillOpacity={1}
                />
              </g>
              <defs>
                <clipPath id='clip0_9392_51688'>
                  <rect width='48' height='48' fill='white' transform='matrix(-1 0 0 1 48.9277 0)' />
                </clipPath>
              </defs>
            </svg>
          </div>
        )}

        <div className={styles.imgContainer}>
          <Splide
            ref={splideRef}
            options={{
              pagination: false,
              arrows: false,
              type: 'fade',
              rewind: true,
            }}
            className={styles.cusSplide}
            onMove={(splide: SplideCore, newIndex: number, prevIndex: number) => {
              setActiveIndex(newIndex);
              handleVideo('video_move', { splide, newIndex, prevIndex });
            }}
          >
            {mainVideoList.map((item, idx) => {
              return (
                <SplideSlide key={item.id}>
                  <div className={styles.videoItem}>
                    <div
                      className={styles.imgMask}
                      style={{ backgroundImage: `url('${item.coverLink}')` }}
                      onClick={() => handleVideo('video_play', { curIndex: idx })}
                    >
                      <div className={styles.playContainer}>
                        <svg width='22' height='26' viewBox='0 0 22 26' fill='none' xmlns='http://www.w3.org/2000/svg'>
                          <path
                            d='M0.665527 3.6825C0.665527 1.33971 3.23082 -0.0989908 5.22988 1.12266L20.4767 10.4402C22.3909 11.61 22.3909 14.3901 20.4767 15.5599L5.22987 24.8773C3.23081 26.099 0.665527 24.6603 0.665527 22.3175V3.6825Z'
                            fill='white'
                          />
                        </svg>
                      </div>
                      <div className={styles.loadingIcon}>
                        <LoadingOutlined style={{ fontSize: '32px', color: '#FFF' }} />
                      </div>
                    </div>

                    <video
                      data-innername={item?.videoInnerName}
                      id='mainVideo'
                      controls
                      playsInline
                      data-mastersrc={item.videoLink}
                      poster={item.coverLink}
                      disablePictureInPicture={!isEnablePIP}
                    />
                  </div>
                </SplideSlide>
              );
            })}
          </Splide>
        </div>
      </div>
    </div>
  );
};

export default MasterVideo;
